import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { OMS_COLORS } from '../omsDashTheme';
import type { PerfNode } from '../utils/performanceModel';
import { formatNumber } from '../utils/formatNumber';
import { BandChip, DeltaChip, ScoreDial } from './HealthScore';
import StatCount from './StatCount';

interface Props {
  nodes: PerfNode[];
  deltaPhrase: string;
  onRemove: (id: string) => void;
  onClear: () => void;
}

/**
 * Side-by-side comparison of 2–4 selected entities — how "who is good vs
 * who is not" becomes visible instead of implied. Slides up from the
 * bottom edge while selection is active.
 */
export default function CompareTray({ nodes, deltaPhrase, onRemove, onClear }: Props) {
  return (
    <AnimatePresence>
      {nodes.length >= 2 && (
        <motion.aside
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          aria-label={`Comparing ${nodes.length} entities`}
          className="fixed inset-x-4 bottom-4 z-40 mx-auto max-w-5xl rounded-2xl border border-[rgba(11,59,46,0.35)] p-3 shadow-[0_24px_60px_-24px_rgba(42,31,23,0.5)] sm:inset-x-8"
          style={{ backgroundColor: 'rgba(250,247,238,0.98)' }}
        >
          <div className="flex items-center justify-between gap-2 px-1 pb-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: OMS_COLORS.champagneDeep }}>
              Compare · {nodes.length} selected
            </p>
            <button
              type="button"
              onClick={onClear}
              className="rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition hover:bg-[rgba(11,59,46,0.06)]"
              style={{ color: OMS_COLORS.tealDark }}
            >
              Clear all
            </button>
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${nodes.length}, minmax(0, 1fr))` }}>
            {nodes.map((node) => {
              const completionPct = node.scheduled > 0 ? Math.round((node.completed / node.scheduled) * 100) : null;
              const passPct = node.completed > 0 ? Math.round((node.counts.pass / node.completed) * 100) : null;
              return (
                <div
                  key={node.id}
                  className="relative rounded-xl border border-[rgba(11,59,46,0.18)] p-3"
                  style={{ backgroundColor: '#F8F2E2' }}
                >
                  <button
                    type="button"
                    aria-label={`Remove ${node.name} from comparison`}
                    onClick={() => onRemove(node.id)}
                    className="absolute right-1.5 top-1.5 rounded p-0.5 transition hover:bg-[rgba(11,59,46,0.08)]"
                    style={{ color: OMS_COLORS.caption }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="flex items-center gap-2.5">
                    <ScoreDial score={node.score} size={44} label={`Health score for ${node.name}`} />
                    <div className="min-w-0">
                      <p className="truncate text-[12px] font-extrabold leading-tight" style={{ color: OMS_COLORS.tealDark }}>
                        {node.name}
                      </p>
                      <p className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.caption }}>
                        Health score
                      </p>
                      <BandChip score={node.score} />
                    </div>
                  </div>
                  {/* Counts block, divided from the score block above. */}
                  <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1.5 border-t border-[rgba(11,59,46,0.12)] pt-2">
                    <StatCount
                      label="Completed"
                      value={node.completed}
                      of={node.scheduled}
                      noun="tasks"
                      size="sm"
                      valueText={completionPct === null ? '—' : undefined}
                    />
                    <StatCount
                      label="Pass rate"
                      value={node.counts.pass}
                      valueText={passPct === null ? '—' : `${passPct}%`}
                      noun={passPct === null ? 'tasks' : `of ${formatNumber(node.completed)} completed`}
                      size="sm"
                    />
                    <StatCount
                      label="Overdue"
                      value={node.overdue}
                      noun="tasks"
                      qualifier="overdue"
                      size="sm"
                      ink={node.overdue > 0 ? '#9F1239' : OMS_COLORS.ink}
                    />
                    <StatCount label="Open" value={node.open} noun="tasks" qualifier="open" size="sm" />
                    <div className="col-span-2 pt-0.5">
                      <DeltaChip delta={node.delta} phrase={`pts ${deltaPhrase}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
